import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { SuccessDialog } from '@bliks-io/ui';
import { useCelebrationStore } from '@/stores/celebration-store';

interface StepFinishedCardOptions {
  slug: string;
  processName: string | undefined;
}

interface StepFinishedCard {
  surveyId: string | undefined;
  processName: string;
}

/**
 * The card after a step is finalized. It stays up until the user acts: the
 * recording refetch that follows the finalize must not move them on.
 */
export function useStepFinishedCard({ slug, processName }: StepFinishedCardOptions) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const recordCompletion = useCelebrationStore((state) => state.recordCompletion);
  const [card, setCard] = useState<StepFinishedCard | null>(null);

  const goToTasks = useCallback(
    (surveyId?: string) =>
      navigate(
        surveyId
          ? { to: '/$slug/my-tasks', params: { slug }, search: { surveyId } }
          : { to: '/$slug/my-tasks', params: { slug } }
      ),
    [navigate, slug]
  );

  const celebrate = useCallback(
    (surveyId: string | undefined) => {
      recordCompletion();
      // Without the process name the card's copy would read "part of “”".
      if (processName === undefined) {
        goToTasks(surveyId);
        return;
      }
      setCard({ surveyId, processName });
    },
    [recordCompletion, processName, goToTasks]
  );

  const surveyId = card?.surveyId;
  const actions = surveyId
    ? {
        primary: { label: t('celebration.step.startSurvey'), onClick: () => goToTasks(surveyId) },
        secondary: { label: t('celebration.later'), onClick: () => goToTasks() }
      }
    : { primary: { label: t('celebration.toTasks'), onClick: () => goToTasks() } };

  const element = card && (
    <SuccessDialog
      open
      title={t('celebration.step.title')}
      description={t('celebration.step.description', { process: card.processName })}
      {...actions}
      onDismiss={() => goToTasks()}
    />
  );

  return { celebrate, isOpen: card !== null, element };
}
